// The DSCF build flow, generated from lib/flow.ts: one lane per task group in
// Andrea's chart order, each task with its steps (minutes per unit, units per
// session), its cure as a wait with no labour, and the shelves it fills; then
// the outputs after final assembly. Light for /tasks and Settings, night for
// the board.
import React from "react";
import type { ShelfRow, TaskRow } from "../lib/types";
import { buildFlowGraph, minutes } from "../lib/flow";
import { TaskChip } from "./TaskChip";
import { Glyph } from "./Glyph";

export interface BuildFlowProps {
  tasks: TaskRow[];
  shelves: ShelfRow[];
  night?: boolean;
  /** Who is on each task today, by task code. */
  peopleByTask?: Record<string, string[]>;
  /** Compact: one line per task (the board), no step list. */
  compact?: boolean;
}

function ShelfNode({ s, from }: { s: ShelfRow; from?: string | null }) {
  const n = s.countMode === "enough" ? (s.level.enough == null ? "–" : s.level.enough ? "enough" : "not enough") : s.level.qty ?? "–";
  return (
    <span className={`flow__shelf flow__shelf--${s.state}`} title={`${s.code} ${s.name}`}>
      <b>{s.code}</b> {s.name} <span className="flow__qty">{n}</span>{from ? <small> from {from}</small> : null}
    </span>
  );
}

export function BuildFlow({ tasks, shelves, night, peopleByTask, compact }: BuildFlowProps) {
  const g = buildFlowGraph(tasks, shelves);
  return (
    <div className={`flow${night ? " flow--night" : ""}${compact ? " flow--compact" : ""}`} data-testid="build-flow">
      {g.lanes.map((lane) => (
        <section key={lane.group} className="flow__lane" aria-label={lane.group}>
          <header className="flow__head">
            <span className="flow__group">{lane.group}</span>
            {lane.floor ? <span className="flow__floor">{lane.floor}</span> : null}
            {lane.note ? <span className="flow__note">{lane.note}</span> : null}
          </header>
          <div className="flow__track">
            {lane.inbound.map((s) => <ShelfNode key={s.code} s={s} from={s.filledBy} />)}
            {lane.tasks.map((ft) => (
              <React.Fragment key={ft.task.code}>
                {ft.drawsFromElsewhere.length && !compact ? (
                  <span className="flow__in">{ft.drawsFromElsewhere.map((s) => s.code).join(" + ")} <span aria-hidden>→</span></span>
                ) : null}
                <div className="flow__task">
                  <div className="flow__task-head">
                    <TaskChip task={ft.task.code} name={ft.task.name} group={ft.task.group} halfOnly={ft.task.halfOnly} night={night} />
                    <span className="flow__sess">{ft.task.sessionQty}/sess</span>
                    {compact && ft.steps.length > 1 ? <span className="flow__sess">{ft.steps.map((s) => s.code).join("→")}</span> : null}
                  </div>
                  {!compact ? (
                    <ol className="flow__steps">
                      {ft.steps.map((st) => (
                        <li key={st.code}>
                          <b>{st.code}</b> {st.name}
                          {st.minutesPerUnit != null ? <span className="flow__min"> {minutes(st.minutesPerUnit)} · {ft.task.sessionQty}/sess</span> : null}
                        </li>
                      ))}
                    </ol>
                  ) : null}
                  {peopleByTask?.[ft.task.code]?.length ? <div className="flow__who">Today: {peopleByTask[ft.task.code].join(", ")}</div> : null}
                </div>
                {ft.cure ? <span className="flow__cure" title="A wait, no labour"><Glyph name="clock" size={night ? 20 : 12} /> {ft.cure}</span> : null}
                {ft.fills.map((s) => <React.Fragment key={s.code}><span className="flow__arrow" aria-hidden>→</span><ShelfNode s={s} /></React.Fragment>)}
              </React.Fragment>
            ))}
            {lane.group === "Final assembly" ? (
              <>
                {g.outputs.ready ? <><span className="flow__arrow" aria-hidden>→</span><ShelfNode s={g.outputs.ready} /></> : null}
                <span className="flow__arrow" aria-hidden>→</span>
                <span className="flow__stage">Provisioning</span>
                <span className="flow__arrow" aria-hidden>→</span>
                <span className="flow__split">
                  {g.outputs.pass ? <ShelfNode s={g.outputs.pass} /> : null}
                  {g.outputs.fail ? <ShelfNode s={g.outputs.fail} /> : null}
                </span>
              </>
            ) : null}
          </div>
        </section>
      ))}
    </div>
  );
}
